import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';

import { CachingStrategyService } from './caching-strategy.service';
import { CACHE_TTL } from './caching.tokens';

interface CacheEntry {
  response: HttpResponse<any>;
  lastRead: number;
  ttl: number;
}

@Injectable()
export class CacheInterceptor implements HttpInterceptor {
  private cache = new Map<string, CacheEntry>();
  private cleanupInterval = 5 * 60 * 1000; // 5 minutes

  constructor(private strategy: CachingStrategyService) {
    this.startCleanup();
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.strategy.isCacheable(req)) {
      return next.handle(req);
    }

    const cacheKey = this.strategy.getCacheKey(req);
    const cached = this.cache.get(cacheKey);
    const ttl = req.context.get(CACHE_TTL) ?? 30000; // fallback 30s

    if (cached && (Date.now() - cached.lastRead) < cached.ttl) {
      return of(cached.response.clone());
    } else {
      if (cached) this.cache.delete(cacheKey);

      return next.handle(req).pipe(
        tap(event => {
          if (event instanceof HttpResponse && event.status === 200) {
            this.cache.set(cacheKey, {
              response: event.clone(),
              lastRead: Date.now(),
              ttl
            });
          }
        })
      );
    }
  }

  private startCleanup(): void {
    setInterval(() => {
      const now = Date.now();
      for (const [key, entry] of this.cache.entries()) {
        if ((now - entry.lastRead) > entry.ttl) {
          this.cache.delete(key);
        }
      }
    }, this.cleanupInterval);
  }
}
